import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Separator } from "@/components/ui/separator";
import { useToast } from "@/hooks/use-toast";
import { 
  ArrowLeft, 
  FileText, 
  DollarSign, 
  HelpCircle, 
  Gavel,
  Calendar,
  MapPin,
  Users,
  Clock
} from "lucide-react";
import { formatFullDateTime } from "@/lib/timezone";

interface PricingData {
  priceArrangement?: string;
  currency?: string;
  customCurrency?: string;
  provideEstimate?: boolean;
  estimateType?: string;
  estimatePrice?: string;
  estimatePriceMin?: string;
  estimatePriceMax?: string;
}

interface Question {
  id?: string;
  question: string;
  explanation?: string;
  isRequired?: boolean;
  type?: string;
  required?: boolean;
  options?: string[];
}

interface QuestionsData {
  questions?: Question[];
}

interface RFP {
  id: string;
  title: string;
  description: string;
  matter_name: string | null;
  region: string | null;
  team: string | null;
  category: string | null;
  budget_range: string | null;
  status: string;
  submission_deadline: string;
  auction_start: string;
  auction_end: string;
  min_bid: number | null;
  min_decrement: number | null;
  created_at: string;
  pricing_data: PricingData | null;
  questions_data: QuestionsData | null;
}

const getCurrencySymbol = (currency?: string): string => {
  const symbols: Record<string, string> = {
    usd: "$",
    gbp: "£",
    eur: "€",
    aed: "د.إ",
    sar: "﷼",
    qar: "﷼",
  };
  return symbols[currency?.toLowerCase() || "usd"] || "$";
};

const getPriceArrangementLabel = (value?: string): string => {
  const labels: Record<string, string> = {
    "fixed-fee": "Fixed Fee",
    "price-cap": "Price Cap",
    "billable-hour": "Billable Hour",
    "other": "Other",
  };
  return labels[value || ""] || value || "Not specified";
};

const RFPFullView = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [rfp, setRfp] = useState<RFP | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchRFP = async () => {
      if (!id) return;

      try {
        const { data, error } = await supabase
          .from("rfps")
          .select("*")
          .eq("id", id)
          .single();

        if (error) throw error;
        setRfp(data as unknown as RFP);
      } catch (error) {
        console.error("Error fetching RFP:", error);
        toast({
          title: "Error",
          description: "Failed to load RFP details",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchRFP();
  }, [id, toast]);

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-12 w-1/3" />
        <Skeleton className="h-64 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  if (!rfp) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">RFP not found</p>
        <Button onClick={() => navigate(-1)} className="mt-4">
          Go Back
        </Button>
      </div>
    );
  }

  const pricing = rfp.pricing_data as PricingData | null;
  const questions = rfp.questions_data as QuestionsData | null;
  const hasReverseAuction = rfp.min_bid || rfp.min_decrement;
  const currencySymbol = getCurrencySymbol(pricing?.currency);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-foreground">{rfp.matter_name || rfp.title}</h1>
          <p className="text-muted-foreground">Full RFP Details</p>
        </div>
        <Badge variant={rfp.status === "AUCTION_ACTIVE" ? "destructive" : "secondary"}>
          {rfp.status.replace(/_/g, " ")}
        </Badge>
      </div>

      {/* Summary Section */}
      <Card>
        <CardHeader className="flex flex-row items-center gap-2">
          <FileText className="h-5 w-5 text-primary" />
          <CardTitle>Summary</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            {rfp.matter_name && (
              <div>
                <p className="text-sm font-medium text-muted-foreground">Matter Name</p>
                <p className="text-foreground">{rfp.matter_name}</p>
              </div>
            )}
            <div>
              <p className="text-sm font-medium text-muted-foreground">Title</p>
              <p className="text-foreground">{rfp.title}</p>
            </div>
            {rfp.region && (
              <div className="flex items-start gap-2">
                <MapPin className="h-4 w-4 text-muted-foreground mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Region</p>
                  <p className="text-foreground">{rfp.region.toUpperCase()}</p>
                </div>
              </div>
            )}
            {rfp.team && (
              <div className="flex items-start gap-2">
                <Users className="h-4 w-4 text-muted-foreground mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Team</p>
                  <p className="text-foreground">{rfp.team.charAt(0).toUpperCase() + rfp.team.slice(1)}</p>
                </div>
              </div>
            )}
            {rfp.category && (
              <div>
                <p className="text-sm font-medium text-muted-foreground">Category</p>
                <p className="text-foreground">{rfp.category}</p>
              </div>
            )}
            {rfp.budget_range && (
              <div>
                <p className="text-sm font-medium text-muted-foreground">Budget Range</p>
                <p className="text-foreground">{rfp.budget_range}</p>
              </div>
            )}
          </div>

          <Separator />

          <div>
            <p className="text-sm font-medium text-muted-foreground mb-2">Description / Scope</p>
            <p className="text-foreground whitespace-pre-wrap">{rfp.description}</p>
          </div>

          <Separator />

          <div className="grid gap-4 md:grid-cols-3">
            <div className="flex items-start gap-2">
              <Calendar className="h-4 w-4 text-muted-foreground mt-0.5" />
              <div>
                <p className="text-sm font-medium text-muted-foreground">Submission Deadline</p>
                <p className="text-foreground text-sm">{formatFullDateTime(rfp.submission_deadline)}</p>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <Clock className="h-4 w-4 text-muted-foreground mt-0.5" />
              <div>
                <p className="text-sm font-medium text-muted-foreground">Auction Start</p>
                <p className="text-foreground text-sm">{formatFullDateTime(rfp.auction_start)}</p>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <Clock className="h-4 w-4 text-muted-foreground mt-0.5" />
              <div>
                <p className="text-sm font-medium text-muted-foreground">Auction End</p>
                <p className="text-foreground text-sm">{formatFullDateTime(rfp.auction_end)}</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Pricing Section */}
      <Card>
        <CardHeader className="flex flex-row items-center gap-2">
          <DollarSign className="h-5 w-5 text-primary" />
          <CardTitle>Pricing</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {pricing ? (
            <>
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Price Arrangement</p>
                  <p className="text-foreground">{getPriceArrangementLabel(pricing.priceArrangement)}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Currency</p>
                  <p className="text-foreground">
                    {pricing.currency === "other" 
                      ? pricing.customCurrency || "Custom" 
                      : pricing.currency?.toUpperCase() || "USD"}
                  </p>
                </div>
              </div>

              {pricing.provideEstimate && (
                <>
                  <Separator />
                  <div>
                    <p className="text-sm font-medium text-muted-foreground mb-2">Estimated Budget</p>
                    {pricing.estimateType === "fixed" ? (
                      <p className="text-lg font-semibold text-foreground">
                        {currencySymbol}{Number(pricing.estimatePrice || 0).toLocaleString()}
                      </p>
                    ) : pricing.estimateType === "range" ? (
                      <p className="text-lg font-semibold text-foreground">
                        {currencySymbol}{Number(pricing.estimatePriceMin || 0).toLocaleString()} — {currencySymbol}{Number(pricing.estimatePriceMax || 0).toLocaleString()}
                      </p>
                    ) : null}
                  </div>
                </>
              )}
            </>
          ) : (
            <p className="text-muted-foreground">No pricing details specified</p>
          )}
        </CardContent>
      </Card>

      {/* Questions Section */}
      <Card>
        <CardHeader className="flex flex-row items-center gap-2">
          <HelpCircle className="h-5 w-5 text-primary" />
          <CardTitle>Questions</CardTitle>
        </CardHeader>
        <CardContent>
          {questions?.questions && questions.questions.length > 0 ? (
            <div className="space-y-4">
              {questions.questions.map((q, index) => (
                <div key={q.id || index} className="p-4 bg-muted/30 rounded-lg">
                  <div className="flex items-start justify-between gap-2">
                    <p className="font-medium text-foreground">
                      {index + 1}. {q.question}
                    </p>
                    {(q.required || q.isRequired) && (
                      <Badge variant="outline" className="text-xs shrink-0">Required</Badge>
                    )}
                  </div>
                  {q.explanation && (
                    <p className="text-sm text-muted-foreground mt-2">
                      {q.explanation}
                    </p>
                  )}
                  {q.options && q.options.length > 0 && (
                    <div className="mt-2">
                      <p className="text-xs text-muted-foreground mb-1">Options:</p>
                      <div className="flex flex-wrap gap-1">
                        {q.options.map((opt, i) => (
                          <Badge key={i} variant="secondary" className="text-xs">
                            {opt}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-muted-foreground">No questions specified</p>
          )}
        </CardContent>
      </Card>

      {/* Reverse Auction Section */}
      {hasReverseAuction && (
        <Card>
          <CardHeader className="flex flex-row items-center gap-2">
            <Gavel className="h-5 w-5 text-primary" />
            <CardTitle>Reverse Auction Settings</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-2">
              {rfp.min_bid && (
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Minimum Allowed Bid</p>
                  <p className="text-lg font-semibold text-foreground">
                    ${rfp.min_bid.toLocaleString()}
                  </p>
                </div>
              )}
              {rfp.min_decrement && (
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Minimum Bid Decrement</p>
                  <p className="text-lg font-semibold text-foreground">
                    ${rfp.min_decrement.toLocaleString()}
                  </p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default RFPFullView;
